import { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookingOrigin, Price, PriceStatus } from '@utriper/core';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { PreviewService } from 'core/api/services/preview';
import Layout from 'components/Layout';
import { Section } from '../styles';
import { Box, Button, Fade, Grid2, IconButton, InputLabel, styled, Typography, useMediaQuery } from '@mui/material';
import { CircularIndeterminate, Chip, PreviewExperience, AlertDialog } from '@utriper/core-ui';
import { usePriceGroupTab } from 'core/contexts/priceGroupTab';
import { Add, Clear, Edit } from '@mui/icons-material';
import PriceGroupForm from './components/PriceGroupForm';
import OriginsForm from './components/OriginsForm';
import PriceGroupGrid from './components/PriceGroupGrid';
import AddUnitForm from './components/AddUnitForm';
import AddRangeForm from './components/AddRangeForm';
import EditUnitForm from './components/EditUnitForm';
import EditRangesForm from './components/EditRangesForm';
import DeleteUnit from './components/DeleteUnit';
import DeleteRange from './components/DeleteRange';

const Container = ({ children, ...props }) => {
    return (
        <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" {...props}>
            {children}
        </Grid2>
    );
};

const Label = styled(InputLabel)({
    fontSize: '16px',
    fontWeight: 400,
    color: '#131313bf',
});

const FORM_IDS = {
    PRICE_GROUP_DATA: 1,
    ORIGINS: 2,
    UNITS: 3,
    RANGE: 4,
    EDIT_UNIT: 5,
    EDIT_RANGES: 6,
    DELETE_UNIT: 7,
    DELETE_RANGE: 8,
};

const PriceGroupIdPage = () => {
    const { id } = useParams();
    const priceGroupId = parseInt(id);
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`price_group_id.${key}`);
    const { getUnitsData, getPriceGroupById, handleDeleteOrigin, loadingPriceGroup, loadingPrices, priceGroup, origins, exitingOrigin, unitsPriceGroup, units } = usePriceGroup();
    const { getStatusColor } = usePriceGroupTab();
    const history = useHistory();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const [openForm, setOpenForm] = useState(false);
    const [formId, setFormId] = useState(0);
    const [unitId, setUnitId] = useState(0);
    const [unitToDelete, setUnitToDelete] = useState(null);
    const [rangeToDelete, setRangeToDelete] = useState(null);
    const canAddUnit = useMemo(() => {
        const existingTypeIds = new Set(unitsPriceGroup.map((u) => u.unitType));
        return units.some((u) => !existingTypeIds.has(u.typeId));
    }, [unitsPriceGroup, units]);

    useEffect(() => {
        getUnitsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getPriceGroupById(priceGroupId, history);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceGroupId]);

    const priceType = Number.isInteger(priceGroup?.type) ? Price.getTranslatedName(Price.types, priceGroup.type, i18n.language) : '';
    const statusLabel = Number.isInteger(priceGroup?.status) ? PriceStatus.getTranslatedName(PriceStatus.status, priceGroup?.status, i18n.language) : '';
    const statusColor = getStatusColor(priceGroup?.status);

    const handleClose = () => {
        setOpenForm(false);
        setFormId(0);
        setUnitId(0);
    };

    const handleOpen = () => setOpenForm(true);

    const handleOpenPriceGroupForm = () => {
        handleOpen();
        setFormId(FORM_IDS.PRICE_GROUP_DATA);
    };

    const handleOpenOriginsForm = () => {
        handleOpen();
        setFormId(FORM_IDS.ORIGINS);
    };

    const handleOpenUnitForm = () => {
        handleOpen();
        setFormId(FORM_IDS.UNITS);
    };

    const handleOpenRangeForm = () => {
        handleOpen();
        setFormId(FORM_IDS.RANGE);
    };

    const handleOpenEditUnitForm = (cell) => {
        if (!Number.isInteger(cell?.unitId)) return; // TODO: ADD ERROR MESSAGE

        setUnitId(cell.unitId);
        setFormId(FORM_IDS.EDIT_UNIT);
        handleOpen();
    };

    const handleOpenEditRangesForm = () => {
        setFormId(FORM_IDS.EDIT_RANGES);
        handleOpen();
    };

    const handleOpenDeleteUnit = (unit) => {
        setUnitToDelete(unit);
        setFormId(FORM_IDS.DELETE_UNIT);
        handleOpen();
    };

    const handleOpenDeleteRange = (range) => {
        setRangeToDelete(range);
        setFormId(FORM_IDS.DELETE_RANGE);
        handleOpen();
    };

    const RenderForm = () => {
        switch (formId) {
            case FORM_IDS.PRICE_GROUP_DATA:
                return <PriceGroupForm priceGroupId={priceGroupId} handleClose={handleClose} />;
            case FORM_IDS.ORIGINS:
                return <OriginsForm priceGroupId={priceGroupId} handleClose={handleClose} />;
            case FORM_IDS.UNITS:
                return <AddUnitForm priceGroupId={priceGroupId} handleClose={handleClose} />;
            case FORM_IDS.RANGE:
                return <AddRangeForm priceGroupId={priceGroupId} handleClose={handleClose} />;
            case FORM_IDS.EDIT_UNIT:
                return <EditUnitForm priceGroupId={priceGroupId} handleClose={handleClose} unitId={unitId} />;
            case FORM_IDS.EDIT_RANGES:
                return <EditRangesForm priceGroupId={priceGroupId} handleClose={handleClose} />;
            case FORM_IDS.DELETE_UNIT:
                return <DeleteUnit priceGroupId={priceGroupId} handleClose={handleClose} unitToDelete={unitToDelete} />;
            case FORM_IDS.DELETE_RANGE:
                return <DeleteRange priceGroupId={priceGroupId} handleClose={handleClose} rangeToDelete={rangeToDelete} />;
            default:
                break;
        }
    };

    return (
        <Layout>
            {loadingPriceGroup ? (
                <CircularIndeterminate centered />
            ) : (
                <Fade in={!loadingPriceGroup}>
                    <Section>
                        {/* Title */}
                        <Grid2 container justifyContent="space-between" alignItems="center" size={12}>
                            <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" size={10}>
                                <Grid2 container justifyContent="flex-start" alignItems="center" gap="8px" size={12} flexWrap="nowrap">
                                    <Typography fontSize={20} fontWeight={900} sx={{ color: '#131313' }} noWrap>
                                        {priceGroup?.name}
                                    </Typography>
                                    <Chip label={statusLabel} sx={{ background: `${statusColor}40`, color: `${statusColor}`, padding: '8px 4px', borderRadius: '4px' }} />
                                </Grid2>
                                {priceGroup?.description && (
                                    <Grid2 container justifyContent="flex-start" alignItems="center" gap="8px" size={12}>
                                        <Typography fontSize={16} fontWeight={500} sx={{ color: '#131313bf' }} noWrap>
                                            {priceGroup.description}
                                        </Typography>
                                    </Grid2>
                                )}
                            </Grid2>
                            <Grid2 container justifyContent="flex-end" alignItems="center" size={2}>
                                {isMobile ? (
                                    <IconButton onClick={handleOpenPriceGroupForm}>
                                        <Edit sx={{ color: '#131313' }} />
                                    </IconButton>
                                ) : (
                                    <Button
                                        sx={{
                                            padding: '8px 14px',
                                            backgroundColor: '#131313',
                                            fontWeight: 600,
                                            width: { xs: '100%', md: 'fit-content' },
                                            textTransform: 'none',
                                        }}
                                        variant="contained"
                                        startIcon={<Edit />}
                                        onClick={handleOpenPriceGroupForm}
                                    >
                                        {translate('edit_button')}
                                    </Button>
                                )}
                            </Grid2>
                        </Grid2>

                        {/* Data */}
                        <Grid2 size={12} container direction="column" mt="32px" gap="16px">
                            <Container
                                size={12}
                                sx={{
                                    '& span': {
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: '#131313',
                                    },
                                }}
                            >
                                <Label>{translate('experience_label')}</Label>
                                <PreviewExperience id={priceGroup?.experienceId} previewService={PreviewService} label="name" />
                            </Container>
                            <Grid2 size={12} container justifyContent="flex-start" alignItems="flex-start">
                                <Container size={6}>
                                    <Label>{translate('currency_label')}</Label>
                                    <Typography fontSize={18} fontWeight={600} sx={{ color: '#131313' }}>
                                        {priceGroup?.currency}
                                    </Typography>
                                </Container>
                                <Container size={6}>
                                    <Label>{translate('price_type_label')}</Label>
                                    <Typography fontSize={18} fontWeight={600} sx={{ color: '#131313' }}>
                                        {priceType}
                                    </Typography>
                                </Container>
                            </Grid2>

                            {/* Origins */}
                            <Container size={12}>
                                <Label>{translate('origins_label')}</Label>
                                <Container size={12} direction="row">
                                    {origins?.length > 0 &&
                                        origins.map((origin) => {
                                            const isExiting = exitingOrigin === origin;
                                            const label = BookingOrigin.getTranslatedName(BookingOrigin.types, origin, i18n.language);
                                            return (
                                                <Chip
                                                    key={origin}
                                                    label={label}
                                                    sx={{
                                                        backgroundColor: '#131313',
                                                        color: '#fff',
                                                        padding: '16px 8px !important',
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        opacity: isExiting ? 0 : 1,
                                                        transition: 'opacity 300ms ease-in-out',
                                                    }}
                                                    deleteIcon={origins?.length > 1 && <Clear sx={{ color: '#fff !important' }} />}
                                                    onDelete={origins?.length > 1 ? () => handleDeleteOrigin(priceGroupId, origin, label) : null}
                                                />
                                            );
                                        })}
                                    {origins?.length < BookingOrigin.getValidOrigins()?.length && (
                                        <Chip
                                            label={translate('add_origin')}
                                            sx={{
                                                backgroundColor: 'var(--color-utriper-blue)',
                                                color: '#fff',
                                                padding: '16px 8px !important',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                transition: 'background-color 0.2s ease-in-out',
                                                '&:hover': {
                                                    backgroundColor: 'var(--color-utriper-blue-dark)',
                                                },
                                            }}
                                            icon={<Add sx={{ color: '#fff !important' }} />}
                                            onClick={handleOpenOriginsForm}
                                        />
                                    )}
                                </Container>
                            </Container>

                            {/* Grid */}
                            {loadingPrices ? <CircularIndeterminate centered /> : <PriceGroupGrid priceGroupId={priceGroupId} handleOpenUnit={handleOpenEditUnitForm} handleOpenRanges={handleOpenEditRangesForm} handleOpenDeleteUnit={handleOpenDeleteUnit} handleOpenDeleteRange={handleOpenDeleteRange} />}

                            {/* Add Buttons */}
                            <Box display="flex" flexDirection="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                {canAddUnit && (
                                    <Button sx={{ color: 'var(--color-utriper-blue)', textTransform: 'none' }} variant="text" startIcon={<Add />} onClick={handleOpenUnitForm}>
                                        {translate('add_unit')}
                                    </Button>
                                )}

                                <Button sx={{ color: 'var(--color-utriper-blue)', textTransform: 'none' }} variant="text" startIcon={<Add />} onClick={handleOpenRangeForm}>
                                    {translate('add_range')}
                                </Button>
                            </Box>
                        </Grid2>
                    </Section>
                </Fade>
            )}

            <AlertDialog open={openForm} handleClose={handleClose} hideTitle hideActions maxWidth="sm" fullWidth dialogContent={<RenderForm />} />
        </Layout>
    );
};

export default PriceGroupIdPage;
